import {ArkivFilnodeModell, ArkivMappenodeModell, ArkivNodeModell, DokumentVersjonModell, EnkelGruppeModell, TilgangsrolleMedlem} from "../tjenester/ressurser/generertNg2HttpKlient";
import {Brukerkontekst} from "felles-ng2/tjenester/Brukerkontekst";
import {isDevMode} from "@angular/core";
import {verifiserErIkkeNil} from "felles-ng2/støttefunksjoner/antakelser";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {sammenliknAlfanumerisk} from "felles-ng2/støttefunksjoner/strenger";
import {erSkjermet} from "felles-ng2/modell/skjermingModellHjelpere";
import {ArkivMappenodeDLM} from "felles-ng2/tjenester/datalager/ArkivDatatjeneste";

export function erMappeNode(node: ArkivNodeModell): node is ArkivMappenodeModell {
    return node.erMappe;
}

export function erFilNode(node: ArkivNodeModell): node is ArkivFilnodeModell {
    return !node.erMappe;
}

/** Returnerer sti til angitt node.
 * Første innslag i returnert liste vil være angitt node,
 * siste innslag vil være gruppe-noden (med {@link ArkivNodeModell#erRotMappe} = true) */
export function stiTilNode(node: ArkivMappenodeModell): ArkivMappenodeModell[];
export function stiTilNode(node: ArkivNodeModell): ArkivNodeModell[];
export function stiTilNode(node: ArkivNodeModell): ArkivNodeModell[] {
    let sti = [node];
    while (erIkkeNil(node.forelder)) {
        node = node.forelder;
        sti.push(node);
    }
    return sti;
}

/** Sorterer arkivnode etter {@link ArkivNodeModell#navn} stigende.
 * Returnerer negativt tall dersom a sorterer før b, positivt dersom a sorterer etter b, 0 dersom like.
 * Må holdes i synk med no.systemfabrikken.lederplan.domene.sak.Beslutning#compareTo */
export function sorterArkivnode(a: ArkivNodeModell, b: ArkivNodeModell): number {
    if (a === b) return 0;

    let sortering = sammenliknAlfanumerisk(a.navn, b.navn);
    if (sortering != 0) return sortering;

    return a.id.localeCompare(b.id);
}

/**
 * Oppretter en syntetisk ArkivMappenodeModell som representerer rot-mappe for en organisasjon.
 * Dens barn vil være gruppene bruker har tilgang til i denne organisasjonen,
 * dvs alle grupper der {@link EnkelGruppeModell#organisasjonsnavn} er lik organisasjonsnavn.
 * @param grupperIOrg liste med grupper knyttet til en og samme organisasjon
 * @param rot mappe med mappe.barn lik alle (på tvers av organisasjoner) gruppe-nodene
 */
export function opprettOrganisasjonsrotMappe(grupperIOrg: EnkelGruppeModell[], rot: ArkivMappenodeModell): ArkivMappenodeModell {
    //opprett en ArkivNodeModell representasjon av alle gruppene i denne organisasjon
    const gruppeNoder = grupperIOrg
      .map(gruppe => {
          const arkivmappe = rot.barn.find(gruppeMappe => gruppeMappe.eiendeGruppeId == gruppe.id);
          isDevMode() && verifiserErIkkeNil(arkivmappe, `fant ikke arkivmappe blant rot.barn for gruppen ${gruppe.id}`);
          return arkivmappe;
      })
      .filter(gruppeMappe => erIkkeNil(gruppeMappe));
    const gruppeIOrg = grupperIOrg[0];
    return {
        id: gruppeIOrg.id + '_organisasjonsmappe',
        navn: gruppeIOrg.organisasjonsnavn,
        forelder: rot,
        erMappe: true,
        erRotMappe: true,
        eiendeGruppeId: gruppeIOrg.id,
        beskrivelse: '',
        skjerming: rot.skjerming,
        barn: gruppeNoder
    } as ArkivMappenodeModell
}

/** Sann dersom angitt mappe er en organisasjonsrot-mappe. Slik mapper har en syntetisk id som ikke peker til noen eksisterende arkivnode i databasen. */
export function erOrganisasjonsrotMappe(mappe: ArkivNodeModell): boolean {
    return mappe.erRotMappe && mappe.id.endsWith('_organisasjonsmappe');
}

/** Sann dersom bruker kan redigere arkivnode */
export function kanArkivnodeRedigeres(arkivnode: ArkivNodeModell, kontekst: Brukerkontekst): boolean {
    //rotmappe kan ikke redigeres
    if (arkivnode.erRotMappe) return false;

    if (kontekst.ikkeInneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, arkivnode.eiendeGruppeId)) return false;

    return true;
}

/** Sann dersom bruker kan legge til nye noder i angitt arkivnode */
export function kanNyNodeLeggesTil(arkivnode: ArkivNodeModell, kontekst: Brukerkontekst): boolean {
    if (kontekst.ikkeInneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, arkivnode.eiendeGruppeId)) return false;

    return true;
}

export function erArkivmappeSkjermet(arkivmappe: ArkivMappenodeDLM): boolean {
    return erSkjermet(arkivmappe);
}

export function stiTilNodeSomTekst(node: ArkivNodeModell): string {
    return stiTilNode(node)
      .reverse()
      .map(node => node.navn)
      .join('/');
}
